import { useState, useMemo } from "react";

/**
 * DrinkImage — resilient <img> wrapper with a per-product branded fallback.
 *
 * If `src` is empty or fails to load, we render an inline SVG that shows:
 *   • the actual product NAME
 *   • the BRAND (if provided) and CATEGORY (e.g. "Scotch")
 *   • an "Image unavailable" label
 *
 * This guarantees that every product card is visually distinct — no two
 * products ever share an identical placeholder image, and we never
 * misrepresent a product by showing the wrong bottle.
 */

// Category → subtle background tint (kept in the Sip Alchemy palette).
const CATEGORY_BG = {
  whisky: "#12100E",
  wine:   "#150A0F",
  beer:   "#141008",
  rum:    "#120E08",
  cocktail: "#0F0D10",
};

const escapeXml = (s = "") =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

// Break a long product name into up to 3 lines that fit the SVG.
function wrapLines(text, maxCharsPerLine = 18, maxLines = 3) {
  const words = String(text || "").trim().split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (test.length > maxCharsPerLine && line) {
      lines.push(line);
      line = w;
      if (lines.length === maxLines - 1) break;
    } else {
      line = test;
    }
  }
  if (line && lines.length < maxLines) lines.push(line);
  // If words remain, append an ellipsis to the last line.
  const consumed = lines.join(" ").split(/\s+/).length;
  if (consumed < words.length && lines.length) {
    lines[lines.length - 1] = `${lines[lines.length - 1]}…`;
  }
  return lines;
}

function buildFallbackSvg({ name, brand, style, category }) {
  const bg = CATEGORY_BG[category] || "#0D0D0D";
  const accent = "#E05A88";
  const cream  = "#F5F3EF";
  const nameLines = wrapLines(name || "Sip Alchemy", 18, 3);
  const subtitleParts = [brand, style].filter(Boolean).map(escapeXml);
  const subtitle = subtitleParts.join(" · ");

  // Layout: bottle silhouette on the left half, text stack on the right.
  const nameStartY = 340 - (nameLines.length - 1) * 26;
  const nameTspans = nameLines
    .map((ln, i) => `<tspan x='300' y='${nameStartY + i * 52}'>${escapeXml(ln)}</tspan>`)
    .join("");

  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 800' preserveAspectRatio='xMidYMid slice'>
    <rect width='600' height='800' fill='${bg}'/>
    <g fill='none' stroke='${accent}' stroke-width='2' opacity='0.35'>
      <rect x='260' y='60' width='80' height='50' rx='4'/>
      <path d='M270 110 Q270 170 240 210 L240 660 Q240 700 300 700 Q360 700 360 660 L360 210 Q330 170 330 110'/>
      <rect x='250' y='300' width='100' height='140' fill='${accent}' opacity='0.08'/>
    </g>
    <text text-anchor='middle' fill='${cream}' font-family='Georgia, "Playfair Display", serif' font-weight='700' font-size='42'>
      ${nameTspans}
    </text>
    ${subtitle ? `<text x='300' y='500' text-anchor='middle' fill='${cream}' opacity='0.7' font-family='"Plus Jakarta Sans", Arial, sans-serif' font-size='18' letter-spacing='2'>${subtitle}</text>` : ""}
    <line x1='240' y1='540' x2='360' y2='540' stroke='${accent}' stroke-width='1' opacity='0.5'/>
    <text x='300' y='585' text-anchor='middle' fill='${accent}' font-family='"Plus Jakarta Sans", Arial, sans-serif' font-size='13' font-weight='700' letter-spacing='4'>IMAGE UNAVAILABLE</text>
    <text x='300' y='730' text-anchor='middle' fill='${cream}' opacity='0.35' font-family='Georgia, "Playfair Display", serif' font-style='italic' font-size='16' letter-spacing='4'>Sip Alchemy</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export default function DrinkImage({
  src,
  alt,
  name,
  brand,
  style,
  category,
  className = "",
  ...rest
}) {
  const [broken, setBroken] = useState(false);
  const fallback = useMemo(
    () => buildFallbackSvg({ name, brand, style, category }),
    [name, brand, style, category]
  );
  const useFallback = broken || !src;
  const displaySrc = useFallback ? fallback : src;
  const displayAlt = useFallback
    ? `${name || "Sip Alchemy"} — image unavailable`
    : alt || `${name || "Sip Alchemy drink"}`;

  return (
    <img
      src={displaySrc}
      alt={displayAlt}
      loading="lazy"
      onError={() => {
        if (!broken) setBroken(true);
      }}
      className={`w-full h-full object-cover bg-ink ${className}`}
      {...rest}
    />
  );
}
